import { useMutation, useSuspenseQuery } from "@apollo/client";
import { getRouteApi } from "@tanstack/react-router";
import { Copy, Pen } from "lucide-react";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BookingStatusBadge } from "../BookingStatusBadge";
import { BookingDocuments } from "@/bookings/BookingDocuments";
import { BookingForm } from "@/bookings/BookingForm";
import { mapBookingToFormData, toBookingInputFields } from "@/bookings/BookingForm/mappers";
import { type BookingFormSchema } from "@/bookings/BookingForm/validation";
import { BookingMetadata, BookingMetadataError, BookingMetadataSkeleton } from "@/bookings/BookingMetadata";
import { BookingSkeleton } from "@/bookings/BookingSkeleton";
import { DuplicateBookingDialog } from "@/bookings/DuplicateBookingDialog";
import { DocumentTileError, DocumentTileSkeleton } from "@/components/DocumentTile";
import { ErrorState } from "@/components/ErrorState";
import { InfoField } from "@/components/InfoField";
import { Heading1 } from "@/components/Typography";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { toDisplayDate } from "@/lib/utils/dates";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { formatAddressWithCompanyName, formatEmptyableValue, formatName } from "@/lib/utils/formatting";

const routeApi = getRouteApi("/(authed)/_layout/bookings/$bookingId");

export type Booking = ResultOf<typeof supplyChainBooking>["supplyChainBooking"];

export default function Booking({ bookingName }: { bookingName: string }) {
  const { bookingId } = routeApi.useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);

  return (
    <>
      <BookingHeader
        bookingName={bookingName}
        onEdit={() => setIsOpen(true)}
        onDuplicate={() => setIsDuplicateOpen(true)}
      />
      <DuplicateBookingDialog bookingId={bookingId} open={isDuplicateOpen} onOpenChange={setIsDuplicateOpen} />

      <Card className="m-4 p-4 bg-muted-light">
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorState message={getErrorMessage(error)} />}>
            <Suspense fallback={<BookingSkeleton numItems={9} aria-label="Loading booking details" />}>
              <BookingDetails bookingId={bookingId} setIsOpen={setIsOpen} isOpen={isOpen} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </Card>

      <hr className="border-border" />
      <div className="bg-muted flex flex-col h-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 items-start">
          <ErrorBoundary FallbackComponent={DocumentTileError}>
            <Suspense fallback={<DocumentTileSkeleton />}>
              <BookingDocuments bookingId={bookingId} />
            </Suspense>
          </ErrorBoundary>

          <ErrorBoundary FallbackComponent={BookingMetadataError}>
            <Suspense fallback={<BookingMetadataSkeleton />}>
              <BookingMetadata bookingId={bookingId} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
}

function BookingHeader({
  bookingName,
  onEdit,
  onDuplicate,
}: {
  bookingName: string;
  onEdit: () => void;
  onDuplicate: () => void;
}) {
  return (
    <header data-testid="booking-header" className="flex justify-between h-16">
      <Heading1 className="truncate px-4 py-2" title={bookingName}>
        {bookingName}
      </Heading1>
      <div className="flex gap-2 m-4">
        <Button variant="outline" onClick={onDuplicate} aria-label="Duplicate Booking">
          <Copy className="h-4 w-4" />
          Duplicate
        </Button>
        <Button variant="outline" onClick={onEdit} aria-label="Edit Booking">
          <Pen className="h-4 w-4" />
          Edit
        </Button>
      </div>
    </header>
  );
}

function BookingDetails({
  bookingId,
  isOpen,
  setIsOpen,
}: {
  bookingId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { data } = useSuspenseQuery(supplyChainBooking, {
    variables: { id: bookingId },
  });
  const [updateSupplyChainBooking] = useMutation(updateSupplyChainBookingMutation);
  const { activeBusinessUnitCountryCode } = useActiveBusinessUnit();
  const { toast } = useToast();

  const onSubmit = async (values: BookingFormSchema) => {
    try {
      const result = await updateSupplyChainBooking({
        variables: {
          input: {
            booking: bookingId,
            ...toBookingInputFields(values),
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error updating ${values.name}`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsOpen(false);

      toast({
        title: "Success",
        description: `Booking ${values.name} updated successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: `Error updating ${values.name}`,
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  const booking = data.supplyChainBooking;
  const bookingInfo = [
    {
      label: "Booking Name",
      value: formatEmptyableValue(booking?.name),
      fullWidth: false,
    },
    {
      label: "Status",
      value: booking?.status ? <BookingStatusBadge status={booking.status} /> : "\u2014",
      fullWidth: false,
    },
    {
      label: "Number of Head",
      value: formatEmptyableValue(booking?.numberOfHead?.toString() || ""),
      fullWidth: false,
    },
    {
      label: "Date Created",
      value: toDisplayDate(booking?.createdAt || undefined, "MM/dd/yyyy"),
      fullWidth: false,
    },
    {
      label: "Planned Shipping Date",
      value: toDisplayDate(booking?.plannedShippingDate || undefined, "MM/dd/yyyy"),
      fullWidth: false,
    },
    {
      label: "Booking Manager",
      value: formatName({
        firstName: booking?.accountManager?.user.firstName,
        lastName: booking?.accountManager?.user.lastName,
      }),
      fullWidth: false,
    },
    {
      label: "Note",
      value: booking?.note ? <div className="whitespace-pre-wrap break-words">{booking.note}</div> : "—",
      fullWidth: Boolean(booking?.note),
    },
    {
      label: "Source",
      value: formatAddressWithCompanyName({
        address: booking?.source?.address,
        companyName: booking?.source?.supplyChainPartner?.businessName,
        businessUnitCountryCode: activeBusinessUnitCountryCode,
      }),
      fullWidth: Boolean(booking?.source?.address),
    },
    {
      label: "Destination",
      value: formatAddressWithCompanyName({
        address: booking?.destination?.address,
        companyName: booking?.destination?.supplyChainPartner?.businessName,
        businessUnitCountryCode: activeBusinessUnitCountryCode,
      }),
      fullWidth: Boolean(booking?.destination?.address),
    },
  ] as const;

  return (
    <>
      <article className="contents" data-testid="booking-details">
        {bookingInfo.map(({ label, value, fullWidth }) => (
          <InfoField key={label} fullWidth={fullWidth}>
            <CardDescription className="text-foreground/80">{label}</CardDescription>
            <CardTitle
              className={typeof value === "string" ? "truncate leading-relaxed" : "leading-relaxed"}
              title={typeof value === "string" ? value : undefined}
            >
              {value}
            </CardTitle>
          </InfoField>
        ))}
      </article>

      <AlertDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
          }
          setIsOpen(open);
        }}
      >
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Booking</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Edit Booking Form</AlertDialogDescription>
          </AlertDialogHeader>
          <BookingForm
            mode="edit"
            onSubmit={onSubmit}
            onCancel={onCancel}
            initialData={mapBookingToFormData(booking)}
          />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const supplyChainBooking = graphql(`
  query GetSupplyChainBooking($id: UUID!) {
    supplyChainBooking(id: $id) {
      id
      name
      status
      numberOfHead
      plannedShippingDate
      note
      accountManager {
        user {
          id
          firstName
          lastName
        }
      }
      source {
        id
        supplyChainPartner {
          id
          businessName
        }
        address {
          id
          line1
          line2
          city
          state
          postcode
          country {
            id
            isoAlpha2
          }
        }
      }
      destination {
        id
        supplyChainPartner {
          id
          businessName
        }
        address {
          id
          line1
          line2
          city
          state
          postcode
          country {
            id
            isoAlpha2
          }
        }
      }
      sexClass
      confirmed
      lotNumber
      accreditations {
        id
        status
        label
      }
      price
      penNumber
      sourceNote
      program {
        id
        label
      }
      grid {
        id
        label
      }
      estimatedWeightNote
      shippedWeight
      veterinarian {
        id
        user {
          id
          firstName
          lastName
        }
      }
      brandNote
      serviceProvided
      certificateNumber
      freightCost
      dateOfSale
      broker {
        id
        businessName
      }
      purchasePricePerCwt
      slide
      shrinkPercent
      breedNote
      dateDelivered
      accountNumber
      riskManagement
      estimatedChoicePercent
      baseWeight
      labels {
        id
        status
        label
      }
      dateOfVisit
      truckingCompany {
        id
        businessName
      }
      condition
      conditions
      ownershipType
      projectedOutDate
      createdAt
    }
  }
`);

export const updateSupplyChainBookingMutation = graphql(`
  mutation UpdateSupplyChainBooking($input: UpdateSupplyChainBookingInput!) {
    updateSupplyChainBooking(input: $input) {
      supplyChainBooking {
        id
        name
        plannedShippingDate
        note
        numberOfHead
        status
        destination {
          id
        }
        source {
          id
        }
        accountManager {
          user {
            id
            firstName
            lastName
          }
        }
        sexClass
        confirmed
        lotNumber
        accreditations {
          id
          status
          label
        }
        price
        penNumber
        sourceNote
        program {
          id
          label
        }
        grid {
          id
          label
        }
        estimatedWeightNote
        shippedWeight
        veterinarian {
          id
          user {
            id
            firstName
            lastName
          }
        }
        brandNote
        serviceProvided
        certificateNumber
        freightCost
        dateOfSale
        broker {
          id
          businessName
        }
        purchasePricePerCwt
        slide
        shrinkPercent
        breedNote
        dateDelivered
        accountNumber
        riskManagement
        estimatedChoicePercent
        truckingCompany {
          id
          businessName
        }
        baseWeight
        labels {
          id
          status
          label
        }
        dateOfVisit
        condition
        conditions
        ownershipType
        projectedOutDate
      }
    }
  }
`);
